/* eslint-disable react-refresh/only-export-components */
import { useState } from 'react';

import { Link, LinkProps } from 'react-router';

import { IconDots } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import {
  Divider as CoreDivider,
  DividerProps as CoreDividerProps,
  IconButton,
  IconButtonProps,
  Popover as PopoverBase,
  PopoverPanelProps,
  PopoverRootProps,
} from '@cfa/react-core';

import { cn } from '@/utils/tailwind';

import { TranslationKey } from '@/i18n';

type Size = 'sm' | 'md' | 'lg' | 'xl' | number;
type IconComponent = React.ComponentType<
  { width?: number; height?: number } | { size?: Size }
>;

interface TxProp {
  /**
   * The translation key of the back button.
   *
   * This is preferred over normal children text.
   */
  tx?: TranslationKey;
}

interface ActionButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, TxProp {
  as?: 'button';
  /** The icon for the action. */
  icon?: IconComponent;
  /** The size of the icon. @default 20 */
  iconSize?: number;
}
interface ActionLinkProps extends TxProp, LinkProps {
  as: 'link';
  /** The icon for the action. */
  icon?: IconComponent;
  /** The size of the action icon. @default 20 */
  iconSize?: number;
  disabled?: boolean;
}

function Action(props: ActionButtonProps): React.ReactNode;
function Action(props: ActionLinkProps): React.ReactNode;
function Action(props: ActionButtonProps | ActionLinkProps): React.ReactNode {
  const {
    icon: IconComponent,
    tx,
    children,
    disabled = false,
    iconSize = 20,
    ...rest
  } = props;
  const { t } = useTranslation();

  const className = cn(
    'flex cursor-pointer items-center gap-3 truncate text-[16px]! font-medium! transition-colors',
    disabled
      ? 'text-disabled-contrast! cursor-not-allowed'
      : 'text-secondary-base! dark:text-contrast! cursor-pointer',
    rest.className,
  );

  const content = (
    <>
      {IconComponent && (
        <IconComponent size={iconSize} width={iconSize} height={iconSize} />
      )}
      {tx ? t(tx) : children}
    </>
  );

  const actionTestIdSuffix =
    IconComponent?.displayName ??
    tx ??
    (typeof children === 'string' ||
    typeof children === 'number' ||
    typeof children === 'bigint' ||
    typeof children === 'boolean'
      ? String(children)
      : 'action');

  return rest.as === 'link' ? (
    <Link
      {...rest}
      className={className}
      onClick={e => {
        if (disabled) {
          e.preventDefault();
        } else {
          rest.onClick?.(e);
        }
      }}
      data-testid={`PopoverMenuAction-${actionTestIdSuffix}`}
    >
      {content}
    </Link>
  ) : (
    <button
      {...rest}
      disabled={disabled}
      className={className}
      data-testid={`PopoverMenuAction-${actionTestIdSuffix}`}
    >
      {content}
    </button>
  );
}

export type ActionProps = ActionLinkProps | ActionButtonProps;

type ActionElement = React.ReactElement<ActionProps, typeof Action>;

/* ------------------------------------------------------------ */

const Divider: React.FC<CoreDividerProps> = props => (
  <CoreDivider {...props} className={cn('my-0!', props.className)} />
);
type DividerElement = React.ReactElement<CoreDividerProps, typeof Divider>;

/* ------------------------------------------------------------- */

type PopoverMenuRootElement =
  | ActionElement
  | DividerElement
  | undefined
  | false
  | '';

export interface PopoverMenuRootProps extends Omit<
  PopoverRootProps,
  'children'
> {
  children?: PopoverMenuRootElement | PopoverMenuRootElement[];
  /** The placement of the popover menu. @default 'bottom start' */
  placement?: PopoverPanelProps['placement'];
  /** The icon for the trigger. @default IconDots */
  icon?: IconComponent;
  triggerButtonClassName?: string;
  panelWrapperClassName?: string;
  /** Props for the trigger button. */
  triggerButtonProps?: IconButtonProps;
  /** Props for the panel. */
  panelProps?: Omit<PopoverPanelProps, 'children'>;
}

const Root: React.FC<PopoverMenuRootProps> = ({
  children,
  icon: IconComponent = IconDots,
  placement = 'bottom start',
  triggerButtonProps,
  panelProps,
  triggerButtonClassName,
  panelWrapperClassName,
  ...rest
}) => {
  const [open, setOpen] = useState(false);

  return (
    <PopoverBase.Root isOpen={open} onOpenChange={setOpen} {...rest}>
      <IconButton
        data-testid="PopoverMenuTrigger"
        className={cn('p-0!', triggerButtonClassName)}
        {...triggerButtonProps}
      >
        <IconComponent size={24} width={24} height={24} />
      </IconButton>
      <PopoverBase.Panel placement={placement} offset={0} {...panelProps}>
        <div
          role="presentation"
          className={cn(
            'flex min-w-[165px] flex-col gap-4',
            panelWrapperClassName,
          )}
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      </PopoverBase.Panel>
    </PopoverBase.Root>
  );
};

interface PopoverMenu {
  /**
   * [Popover Documentation](https://design-system.cfahome.com/components/popover/react)
   *
   * @example
   * <PopoverMenu.Root>
   *   <PopoverMenu.Action
   *     as="link"
   *     to={routes.plans.managedPlans}
   *     icon={IconUserPlus}
   *     tx="Generic.assign"
   *   />
   *   <PopoverMenu.Action
   *     icon={IconUsersGroup}
   *     tx="Button.trainTogether"
   *   />
   *   <PopoverMenu.Divider />
   *   <PopoverMenu.Action
   *     icon={IconPrinter}
   *     tx="Button.printReport"
   *   />
   * </PopoverMenu.Root>
   */
  Root: typeof Root;
  /**
   * A popover menu action component that can be used to display a single action.
   *
   * @default as="button"
   *
   * @example
   * <PopoverMenu.Action icon={IconUsersGroup} tx="Button.trainTogether" />
   * <PopoverMenu.Action icon={IconUsersGroup}>Train Together</PopoverMenu.Action>
   * <PopoverMenu.Action as="link" to={routes.plans.managedPlans} icon={IconUserPlus} tx="Generic.assign" />
   */
  Action: typeof Action;
  /**
   * [Divider Documentation](https://design-system.cfahome.com/components/divider/react)
   *
   * @example
   * <PopoverMenu.Divider />
   */
  Divider: typeof Divider;
}

/**
 * A composeable popover menu used to display a list of actions in a dropdown menu.
 *
 * [Popover Documentation](https://design-system.cfahome.com/components/popover/react)
 *
 * @example
 * <PopoverMenu.Root>
 *   <PopoverMenu.Action
 *     as="link"
 *     to={routes.plans.managedPlans}
 *     icon={IconUserPlus}
 *     tx="Generic.assign"
 *   />
 *   <PopoverMenu.Action
 *     icon={IconUsersGroup}
 *     tx="Button.trainTogether"
 *   />
 *   <PopoverMenu.Divider />
 *   <PopoverMenu.Action
 *     icon={IconPrinter}
 *     tx="Button.printReport"
 *   />
 * </PopoverMenu.Root>
 */
export const PopoverMenu: PopoverMenu = {
  Root,
  Action,
  Divider,
};
